/**
 * Resolves a PNG image import to a URL string.
 *
 * Next.js transforms PNG imports into objects: { src: string, width, height }
 * tsup (used for the WordPress IIFE bundle) resolves them as plain strings.
 * This helper handles both shapes so image imports work in all environments.
 */
export const resolveImageSrc = (icon: { src: string } | string): string => (typeof icon === 'string' ? icon : icon.src);

const HEIC_JPEG_QUALITY = 0.9;

/**
 * Transcodes an Apple HEIC/HEIF photo to JPEG in the browser.
 *
 * The decoder is a ~2MB libheif wasm bundle, so it is imported lazily — only a visitor who actually
 * picks a HEIC file pays for it. `heic-to/next` is the Next.js-safe build, which loads the wasm
 * without relying on bundler-specific asset handling.
 */
export const convertHeicToJpeg = async (file: File): Promise<Blob> => {
  const { heicTo } = await import('heic-to/next');

  return heicTo({ blob: file, type: 'image/jpeg', quality: HEIC_JPEG_QUALITY });
};

/** Long enough for a browser to parse the header of a large clip, short enough not to hang the picker. */
const VIDEO_METADATA_TIMEOUT_MS = 10_000;

/** Rejects rather than guessing: a zero would silently pass every length check. */
export const readVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');

    const settle = (outcome: () => void) => {
      clearTimeout(timeoutId);
      video.removeAttribute('src');
      URL.revokeObjectURL(objectUrl);
      outcome();
    };

    const timeoutId = setTimeout(
      () => settle(() => reject(new Error('Timed out reading the video metadata'))),
      VIDEO_METADATA_TIMEOUT_MS,
    );

    video.onloadedmetadata = () =>
      settle(() =>
        // A stream with no known length reports Infinity, which no comparison can act on.
        Number.isFinite(video.duration) ? resolve(video.duration) : reject(new Error('Video duration is unknown')),
      );
    video.onerror = () => settle(() => reject(new Error('Could not read the video')));

    video.preload = 'metadata';
    video.src = objectUrl;
  });
